import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { adminTeamFilters, type AdminTeamFilters } from '@gg/shared';
import { Badge, Notice, SelectField, Table, Td, TextField, Th } from '@/components/ui';
import { teamStatusTone } from '@/lib/adminTones';
import { formatDateTime } from '@/lib/dates';
import { useAdminTeams } from '@/lib/queries';
import { teamMembersLabel } from '@/lib/teamLabels';

/**
 * A-12: every team of the event with its captain and completeness, filtered by the API on
 * format, incomplete (forming), no captain and a search on the name or any seat's address.
 */
export function AdminTeams() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  // Filters live in the URL like the registrations list; unknown values are dropped.
  const parsed = adminTeamFilters.safeParse(Object.fromEntries(params));
  const filters: AdminTeamFilters = parsed.success ? parsed.data : {};

  const setFilter = (key: keyof AdminTeamFilters, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  // The search box types freely and updates the URL after a pause.
  const [q, setQ] = useState(filters.q ?? '');
  useEffect(() => {
    const id = setTimeout(() => {
      if (q.trim() !== (params.get('q') ?? '')) setFilter('q', q.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const list = useAdminTeams(filters);
  const teams = list.data?.teams ?? [];

  return (
    <section className="space-y-6">
      <h1 className="font-display text-section-lg font-bold">{t('admin.teams.title')}</h1>
      <Notice>{t('admin.teams.intro')}</Notice>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          label={t('admin.teams.search')}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <SelectField
          label={t('admin.filter.format')}
          value={filters.formatId ?? ''}
          onChange={(e) => setFilter('formatId', e.target.value)}
        >
          <option value="">{t('admin.filter.all')}</option>
          {list.data?.formats.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('admin.teams.filter.status')}
          value={filters.status ?? ''}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="">{t('admin.filter.all')}</option>
          <option value="incomplete">{t('admin.teams.filter.incomplete')}</option>
          <option value="complete">{t('admin.teamStatus.complete')}</option>
          <option value="cancelled">{t('admin.teamStatus.cancelled')}</option>
        </SelectField>
        <SelectField
          label={t('admin.teams.col.captain')}
          value={filters.captain ?? ''}
          onChange={(e) => setFilter('captain', e.target.value)}
        >
          <option value="">{t('admin.filter.all')}</option>
          <option value="none">{t('admin.teams.noCaptain')}</option>
        </SelectField>
      </div>

      {list.isError && (
        <p role="alert" className="text-sm text-danger">
          {t('admin.teams.error')}
        </p>
      )}
      {list.data &&
        (teams.length === 0 ? (
          <p className="py-8 text-center text-ink-muted">
            {t(Object.keys(filters).length ? 'admin.teams.noMatch' : 'admin.teams.empty')}
          </p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('admin.teams.col.name')}</Th>
                <Th>{t('admin.teams.col.format')}</Th>
                <Th>{t('admin.teams.col.captain')}</Th>
                <Th>{t('admin.teams.col.members')}</Th>
                <Th>{t('admin.teams.col.status')}</Th>
                <Th>{t('admin.teams.col.created')}</Th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.id}>
                  <Td>
                    <Link to={`/admin/teams/${team.id}`} className="font-semibold">
                      {team.name}
                    </Link>
                  </Td>
                  <Td>{team.formatName}</Td>
                  <Td>
                    {team.captain ? (
                      team.captain.email
                    ) : (
                      <Badge tone="danger">{t('admin.teams.noCaptain')}</Badge>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap">{teamMembersLabel(t, team)}</Td>
                  <Td>
                    <Badge tone={teamStatusTone(team.status)}>
                      {t(`admin.teamStatus.${team.status}`)}
                    </Badge>
                  </Td>
                  <Td className="whitespace-nowrap text-ink-muted">
                    {formatDateTime(team.createdAt)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ))}
    </section>
  );
}
